import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useOrder } from '../hooks/api-hooks';
import { ErrorState, Loading } from '../components/States';
import { formatCountdown, formatDateTime, formatPrice, msUntil } from '../lib/format';
import { recallPayment, rememberPayment } from '../lib/payment-store';
import type { PaymentInstructions } from '../lib/types';

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Chờ chuyển khoản',
  PAID: 'Đã thanh toán',
  EXPIRED: 'Đã hết hạn',
  CANCELLED: 'Đã huỷ',
  REVIEW_REQUIRED: 'Cần đối soát thủ công',
};

export function OrderPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const order = useOrder(orderId);

  // POST /checkout returns the bank details once; keep them across reloads.
  const fromNavigation = (location.state as { payment?: PaymentInstructions } | null)?.payment;
  const [payment, setPayment] = useState<PaymentInstructions | null>(
    fromNavigation ?? (orderId ? recallPayment(orderId) : null),
  );

  useEffect(() => {
    if (orderId && fromNavigation) {
      rememberPayment(orderId, fromNavigation);
      setPayment(fromNavigation);
    }
  }, [orderId, fromNavigation]);

  const [remaining, setRemaining] = useState<number | null>(null);
  const [copied, setCopied] = useState<'account' | 'content' | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const expiresAt = order.data?.expiresAt;
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setRemaining(msUntil(expiresAt));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  async function copyValue(value: string, field: 'account' | 'content') {
    setCopyError(null);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement('textarea');
        input.value = value;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        const succeeded = document.execCommand('copy');
        input.remove();
        if (!succeeded) throw new Error('Không thể sao chép');
      }
      setCopied(field);
    } catch {
      setCopied(null);
      setCopyError('Không thể tự sao chép. Vui lòng chọn và sao chép thủ công.');
    }
  }

  if (order.isPending) return <Loading />;
  if (order.error) return <ErrorState error={order.error} />;
  if (!order.data) return <ErrorState error={new Error('Không tìm thấy thông tin đơn hàng.')} />;

  const data = order.data;
  const isPending = data.status === 'PENDING';

  return (
    <section aria-labelledby="order-title">
      <h1 id="order-title">Đơn hàng {data.orderCode}</h1>
      <p
        className={data.status === 'PAID' ? 'success' : 'muted'}
        role="status"
        aria-live="polite"
      >
        Trạng thái: {STATUS_LABEL[data.status] ?? data.status}
      </p>

      {data.items && (
        <ul className="list">
          {data.items.map((item) => (
            <li key={item.id}>
              <span>{item.productTitle}</span>
              <span>{formatPrice(item.finalPrice)}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="muted">Tạm tính: {formatPrice(data.subtotal)}</p>
      {data.discountAmount > 0 && (
        <p className="muted">Giảm giá: −{formatPrice(data.discountAmount)}</p>
      )}
      <p className="price">Tổng thanh toán: {formatPrice(data.total)}</p>

      {isPending && (
        <section className="panel" aria-labelledby="payment-title">
          <h2 id="payment-title">Hướng dẫn chuyển khoản</h2>
          <p>
            Đơn hết hạn sau{' '}
            <strong>{remaining === null ? 'đang tính…' : formatCountdown(remaining)}</strong> (
            {formatDateTime(data.expiresAt)}).
          </p>
          {remaining !== null && remaining <= 0 ? (
            <p className="error" role="alert">
              Thời gian thanh toán đã hết. Không chuyển khoản cho đơn này; hệ thống đang cập nhật
              trạng thái chính thức.
            </p>
          ) : payment ? (
            <dl>
              <dt>Ngân hàng</dt>
              <dd>{payment.bankCode}</dd>
              <dt>Số tài khoản</dt>
              <dd>
                <code>{payment.accountNumber}</code>{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => void copyValue(payment.accountNumber, 'account')}
                >
                  Sao chép
                </button>
              </dd>
              <dt>Chủ tài khoản</dt>
              <dd>{payment.accountName}</dd>
              <dt>Số tiền</dt>
              <dd>{formatPrice(data.total)}</dd>
              <dt>Nội dung chuyển khoản</dt>
              <dd>
                <code>{payment.transferContent}</code>{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => void copyValue(payment.transferContent, 'content')}
                >
                  Sao chép
                </button>
              </dd>
            </dl>
          ) : (
            <p className="muted">
              Thông tin ngân hàng không còn trong phiên này. Hãy mở lại tab đã tạo đơn hoặc liên
              hệ hỗ trợ trước khi chuyển khoản; không tự suy đoán số tài khoản hay nội dung.
            </p>
          )}
          {copied && (
            <p className="success" role="status" aria-live="polite">
              Đã sao chép {copied === 'account' ? 'số tài khoản' : 'nội dung chuyển khoản'}.
            </p>
          )}
          {copyError && <p className="error" role="alert">{copyError}</p>}
          {remaining !== null && remaining > 0 && (
            <p className="muted">
              Chuyển đúng số tiền và giữ nguyên nội dung để SePay đối soát tự động. Trang này tự
              kiểm tra lại mỗi 5 giây; chỉ trạng thái “Đã thanh toán” mới xác nhận giao dịch.
            </p>
          )}
        </section>
      )}

      {data.status === 'PAID' && (
        <p>
          <Link to="/thu-vien">Mở thư viện của bạn</Link>
        </p>
      )}
    </section>
  );
}
